<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import {
  fetchPackages, topupCheckout,
  type GoldPackage, type SpecialOffer, type CustomTerms, type KycTerms, type PromoQuote,
} from '../game/topup-api';
import PromoCodeField from './PromoCodeField.vue';
import { resolvePromo, minOrderLabel, type PromoProduct } from './promo';
import type { ConfirmRow } from '../game/confirm';
import runesIcon from '../assets/fimbulwake/icons/runes.webp';
import { toast } from '../game/toast';
import { confirmAction } from '../game/confirm';
// Named for what each render DEPICTS, not for the tier that happens to use it.
// The filenames are inherited and misleading: pack-stack.webp is an iron-bound
// CHEST and pack-roll.webp is a small leather POUCH, so binding tiers to them by
// name put the chest on the 500 pack ("A Pouch of Runes") and the pouch on the
// 1000 pack ("A Chest of Runes") — each contradicting its own label, and the
// shelf shrinking as it got more expensive. Depicted-object names make the
// escalation checkable at a glance: pouch -> chest -> runic chest -> hoard.
import artPouch from '../assets/fimbulwake/shop/pack-roll.webp';
import artChest from '../assets/fimbulwake/shop/pack-stack.webp';
import artRunicChest from '../assets/fimbulwake/shop/pack-satchel.webp';
import artHoard from '../assets/fimbulwake/shop/pack-vault.webp';
import { t } from '../i18n';
import { formatMoney, formatNumber, effectiveCurrency } from '../i18n/money';

const props = defineProps<{ balance?: number; spotlight?: boolean }>();
const emit = defineEmits<{ (e: 'updated'): void }>();

type Tab = 'buy' | 'deals';
const tab = ref<Tab>('buy');

const packages = ref<GoldPackage[]>([]);
const offers = ref<SpecialOffer[]>([]);
const custom = ref<CustomTerms | null>(null);
const kyc = ref<KycTerms | null>(null);
/** Server-reported: is the mock provider still the active one? Gates every
 *  "no real charge is made" line. Defaults to FALSE so a failed/slow load never
 *  flashes a test-mode promise over a live checkout (QA 86cb9yd3p item 4). */
const testMode = ref(false);

/** The credited-bars row for a gold-buy confirm — no commission, 1:1 pay-in. */
function breakdownRows(bars: number) {
  return [
    { label: t('shop.confirm.credited'), value: formatNumber(bars), icon: runesIcon, tone: 'success' as const },
  ];
}
/** The confirm note — a standard reminder on the confirm dialog. It says the
 *  card WILL be charged unless the mock provider is the one taking the money. */
const commissionNote = () => t(testMode.value ? 'shop.confirm.noteTest' : 'shop.confirm.note');
// KYC gate active for this player: big spends will be refused until approved.
const kycGate = computed(() =>
  !!kyc.value && kyc.value.enabled && kyc.value.status !== 'APPROVED');
const loading = ref(true);
const error = ref('');
const busy = ref<string | null>(null);
const justGranted = ref<number | null>(null); // bars from the last inline mock settle

// ── Custom amount ──
const customBars = ref(1000);
const customCents = computed(() =>
  custom.value ? Math.max(1, Math.round(customBars.value * custom.value.centsPerBar)) : 0);
const customValid = computed(() =>
  !!custom.value && Number.isInteger(customBars.value)
  && customBars.value >= custom.value.minBars && customBars.value <= custom.value.maxBars);

// ── Deal countdown — the "limited time" pressure. The catalog itself no longer
// rotates (dominion parity: all 20 deals stay on the board); the clock just
// resets every 8 hours, exactly like the reference's midnight countdown. ──
const ROTATION_MS = 8 * 60 * 60 * 1000;
const offersEndAt = ref<number | null>(null);
const countdown = ref('');
let countdownTimer = 0;
function tickCountdown() {
  const now = Date.now();
  // Server sends the window end; the epoch-based fallback lands on the same boundary.
  const end = offersEndAt.value ?? (Math.floor(now / ROTATION_MS) + 1) * ROTATION_MS;
  const diff = Math.max(0, end - now);
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  countdown.value = `${pad(h)}:${pad(m)}:${pad(s)}`;
  // The window rolled over while the tab was open — pull the fresh slate.
  if (diff === 0 && !refreshingOffers) void refreshOffers();
}

let refreshingOffers = false;
async function refreshOffers() {
  refreshingOffers = true;
  try {
    const v = await fetchPackages();
    offers.value = v.offers;
    offersEndAt.value = v.offersEndAt;
  } catch { /* keep the stale slate; next tick retries */ }
  finally { refreshingOffers = false; }
}

onMounted(async () => {
  tickCountdown();
  countdownTimer = window.setInterval(tickCountdown, 1000);
  try {
    const v = await fetchPackages();
    packages.value = v.packages;
    offers.value = v.offers;
    offersEndAt.value = v.offersEndAt ?? null;
    custom.value = v.custom;
    kyc.value = v.kyc ?? null;
    testMode.value = v.testMode === true;
    customBars.value = Math.max(v.custom.minBars, 1000);
  }
  catch (e) { error.value = (e as Error).message; }
  finally { loading.value = false; }
});
onUnmounted(() => { if (countdownTimer) window.clearInterval(countdownTimer); });

// Params renamed off `t` — it is the translate function in this file now.
function openTab(next: Tab) { tab.value = next; }

/** Shelf price tags — the player's display currency, converted if a rate is known. */
const price = (cents: number) => formatMoney(cents);

/** The figure on a confirm button — same display currency as the shelf. */
const chargePrice = (cents: number) => formatMoney(cents);

/** Per-tier dressing — ported 1:1 from dominion's PACKS. */
interface TierLook {
  art: string; color: string;
  /** A translation KEY, not display text — resolved at render so it follows the switcher. */
  ribbon?: string;
  ribbonIcon?: string;
  card?: 'popular' | 'whale';
  btn: 'grey' | 'gold' | 'purple' | 'bright';
}
/** Tier look -> button-system variant. The premium tiers earn the lit primary
 *  (.btn-forge--rune); the plain tiers stay a forged tab, so the price ladder
 *  still reads at a glance. Replaces four per-tier COLOUR skins (purple-black,
 *  brass, green, bright-brass) that were art-deco leftovers and had nothing to do
 *  with what the tiers mean. */
const buyVariant = (btn: string) => (btn === 'grey' || btn === 'purple' ? '' : 'btn-forge--rune');

const TIERS: Record<string, TierLook> = {
  stack:   { art: artPouch,   color: '#c8a24c', btn: 'grey' },
  roll:    { art: artChest,    color: '#e3c878', btn: 'gold', ribbon: 'shop.ribbon.popular', ribbonIcon: '★', card: 'popular' },
  satchel: { art: artRunicChest, color: '#9a86ab', btn: 'purple' },
  vault:   { art: artHoard,   color: '#e3c878', btn: 'bright', ribbon: 'shop.ribbon.bestValue', ribbonIcon: '♛', card: 'whale' },
};
function tier(p: GoldPackage, idx: number): TierLook {
  return TIERS[p.id] ?? Object.values(TIERS)[Math.min(idx, 3)];
}

/** Deal cards borrow the pack renders, tiered by bar size, so offers look like
 *  premium bundles (art + a gold-family accent) instead of flat coloured chips. */
function dealTier(bars: number): { art: string; color: string } {
  if (bars < 1000)  return { art: artPouch,      color: '#c8a24c' };
  if (bars < 3000)  return { art: artChest,      color: '#dcb85e' };
  if (bars < 10000) return { art: artRunicChest, color: '#e3c878' };
  return { art: artHoard, color: '#f0dda4' };
}

/** Presentational only — arranges the hoard shelf: pulls the 'whale' (best-value)
 *  tier out of source order and reseats it centre-ish among its siblings, so it
 *  renders as the raised centrepiece. The v-for below still keys on p.id and
 *  still calls buy(p)/tier(p, item.idx) on the untouched package object — this
 *  only changes render ORDER, never which packages exist or what buying does. */
interface ShelfItem { p: GoldPackage; idx: number; look: TierLook; hero: boolean; }
const shelfPacks = computed<ShelfItem[]>(() => {
  const all = packages.value.map((p, idx) => ({ p, idx, look: tier(p, idx) }));
  const heroPos = all.findIndex(x => x.look.card === 'whale');
  if (heroPos === -1) return all.map(x => ({ ...x, hero: false }));
  const heroItem = { ...all[heroPos], hero: true };
  const rest = all.filter((_, i) => i !== heroPos).map(x => ({ ...x, hero: false }));
  rest.splice(Math.floor(rest.length / 2), 0, heroItem);
  return rest;
});

const redirecting = ref(false);

// ── Promo code ──
// The player keeps one code for the whole shop. It is checked against the exact
// purchase at click time (not when typed): switching product re-validates for free,
// and a code that expired meanwhile is caught before the confirm, not at the gateway.
const promoCode = ref('');
const promoErrorKey = ref('');

function applyPromo(code: string) { promoCode.value = code; promoErrorKey.value = ''; }
function removePromo() { promoCode.value = ''; promoErrorKey.value = ''; }

/** The quote for this purchase: a PromoQuote, null (no code), or false (blocked — already shown to the player). */
async function quoteFor(product: PromoProduct, busyKey: string): Promise<PromoQuote | null | false> {
  promoErrorKey.value = '';
  if (!promoCode.value) return null;
  busy.value = busyKey;
  try {
    const r = await resolvePromo(promoCode.value, product);
    if (r.kind === 'refused') {
      promoErrorKey.value = r.reasonKey;
      toast(t(r.reasonKey, { amount: minOrderLabel() }), { kind: 'error' });
      return false;
    }
    if (r.kind === 'failed') { error.value = r.message; return false; }
    return r.kind === 'quote' ? r.quote : null;
  } finally { busy.value = null; }
}

/** The confirm's closing rows: the plain total, or full price / discount / total under a code. */
function totalRows(plainTotal: ConfirmRow, q: PromoQuote | null): ConfirmRow[] {
  if (!q) return [plainTotal];
  return [
    { label: t('shop.promo.fullPrice'), value: chargePrice(q.fullCents), tone: 'dim' },
    { label: t('shop.promo.discount', { code: q.code }), value: `\u2212${chargePrice(q.discountCents)}`, tone: 'success' },
    { label: t('shop.confirm.total'), value: chargePrice(q.priceCents), tone: 'bright' },
  ];
}

async function settle(res: { completed: boolean; url: string; bars: number }) {
  if (res.completed) {
    justGranted.value = res.bars;
    toast(t('shop.toast.granted', { amount: `+${formatNumber(res.bars)}` }), { icon: runesIcon });
    emit('updated');
  } else {
    redirecting.value = true;
    window.location.href = res.url;
  }
}

async function buy(p: GoldPackage) {
  if (busy.value) return;
  const promo = await quoteFor({ packageId: p.id }, p.id);
  if (promo === false) return;
  const payCents = promo?.priceCents ?? p.priceCents;
  const ok = await confirmAction({
    kicker: t('shop.confirm.kicker'),
    title: t('shop.confirm.buyTitle', { name: p.name }),
    icon: tier(p, 0).art,
    rows: [
      ...breakdownRows(p.bars),
      ...totalRows({ label: t('shop.confirm.total'), value: chargePrice(p.priceCents), tone: 'bright' }, promo),
    ],
    note: commissionNote(),
    checkout: true,
    confirmLabel: t('shop.confirm.pay', { amount: chargePrice(payCents) }),
  });
  if (!ok) return;
  busy.value = p.id; error.value = ''; justGranted.value = null;
  try { await settle(await topupCheckout({ packageId: p.id, currency: effectiveCurrency(), promoCode: promo?.code })); }
  catch (e) { error.value = (e as Error).message; }
  finally { busy.value = null; }
}

async function buyCustom() {
  if (busy.value || !customValid.value) return;
  const promo = await quoteFor({ bars: customBars.value }, 'custom');
  if (promo === false) return;
  const payCents = promo?.priceCents ?? customCents.value;
  const ok = await confirmAction({
    kicker: t('shop.confirm.kicker'),
    title: t('shop.confirm.customTitle', { count: formatNumber(customBars.value) }),
    icon: runesIcon,
    rows: [
      ...breakdownRows(customBars.value),
      ...totalRows({ label: t('shop.confirm.total'), value: chargePrice(customCents.value), tone: 'bright' }, promo),
    ],
    note: commissionNote(),
    checkout: true,
    confirmLabel: t('shop.confirm.pay', { amount: chargePrice(payCents) }),
  });
  if (!ok) return;
  busy.value = 'custom'; error.value = ''; justGranted.value = null;
  try { await settle(await topupCheckout({ bars: customBars.value, currency: effectiveCurrency(), promoCode: promo?.code })); }
  catch (e) { error.value = (e as Error).message; }
  finally { busy.value = null; }
}

async function buyOffer(o: SpecialOffer) {
  if (busy.value) return;
  const promo = await quoteFor({ offerId: o.id }, o.id);
  if (promo === false) return;
  const payCents = promo?.priceCents ?? o.priceCents;
  const ok = await confirmAction({
    kicker: t('shop.confirm.offerKicker'),
    title: t('shop.confirm.grabTitle', { name: o.name }),
    icon: runesIcon,
    rows: [
      ...breakdownRows(o.bars),
      { label: t('shop.confirm.was'), value: chargePrice(o.originalCents), tone: 'dim' },
      ...totalRows(
        { label: t('shop.confirm.totalSaving', { percent: o.savings }), value: chargePrice(o.priceCents), tone: 'bright' },
        promo,
      ),
    ],
    note: commissionNote(),
    checkout: true,
    confirmLabel: t('shop.confirm.pay', { amount: chargePrice(payCents) }),
  });
  if (!ok) return;
  busy.value = o.id; error.value = ''; justGranted.value = null;
  try { await settle(await topupCheckout({ offerId: o.id, currency: effectiveCurrency(), promoCode: promo?.code })); }
  catch (e) { error.value = (e as Error).message; }
  finally { busy.value = null; }
}
</script>

<template>
  <section class="relative">
    <!-- Redirect overlay — shown while the browser navigates to the payment provider -->
    <div v-if="redirecting" class="absolute inset-0 z-50 grid place-items-center bg-bg/90 backdrop-blur-sm">
      <div class="text-center">
        <div class="gr-spinner mb-3"></div>
        <p class="font-heading text-lg text-gold">{{ t('shop.redirecting.title') }}</p>
        <p class="font-body text-sm text-text-dim mt-1">{{ t('shop.redirecting.desc') }}</p>
      </div>
    </div>

    <!-- header: kicker + vault title, balance pill on the right (dominion ShopPage) -->
    <div class="flex flex-wrap items-end justify-between gap-4 pb-4 border-b border-border/70">
      <div>
        <div class="font-label uppercase tracking-[0.3em] text-[9px] text-text-dim">{{ t('shop.kicker') }}</div>
        <h1 class="flex items-center gap-3 font-heading font-black text-[28px] text-[#f0e6d0] tracking-[0.05em] leading-tight">
          <img :src="runesIcon" class="w-7 h-7 gr-gem" alt="" />
          {{ t('shop.title') }}
        </h1>
      </div>
      <div class="gr-balance" v-if="props.balance != null">
        <img :src="runesIcon" class="w-4 h-4" alt="" />
        <div>
          <div class="font-heading text-[10px] tracking-[0.1em] uppercase text-[#bccbdb] leading-none whitespace-nowrap">{{ t('shop.balance') }}</div>
          <div class="font-heading font-black text-lg text-gold leading-tight">{{ formatNumber(props.balance) }}</div>
        </div>
      </div>
    </div>

    <!-- tab rail (dominion's buy/history) -->
    <nav class="flex flex-wrap items-center gap-1 border-b border-border/60 mb-6">
      <!-- rail scrolls sideways on mobile (hdr-rail hides the scrollbar) -->
      <button :class="['notch-tab shrink-0 whitespace-nowrap', tab === 'buy' && 'notch-tab-active']" @click="openTab('buy')">{{ t('shop.tabs.buy') }}</button>
      <button :class="['notch-tab shrink-0 whitespace-nowrap', tab === 'deals' && 'notch-tab-active']" @click="openTab('deals')">{{ t('shop.tabs.deals') }}</button>
    </nav>

    <p v-if="loading" class="text-text-dim">{{ t('common.loading') }}</p>
    <p v-if="error" class="mb-3 text-sm text-error">{{ error }}</p>

    <!-- KYC purchase gate (dominion parity): warn before the server refuses -->
    <div v-if="kycGate && (tab === 'buy' || tab === 'deals')" class="gr-kyc">
      <span class="gr-kyc-badge">{{ t('shop.kyc.badge') }}</span>
      <!-- The threshold is a real limit the server enforces, so it is shown in the
           charge currency — an estimate could sit either side of the line. -->
      <span>{{ kyc!.status === 'PENDING'
        ? t('shop.kyc.textPending', { amount: chargePrice(kyc!.thresholdCents) })
        : t('shop.kyc.text', { amount: chargePrice(kyc!.thresholdCents) }) }}</span>
    </div>

    <PromoCodeField
      v-if="!loading && (tab === 'buy' || tab === 'deals')"
      :applied="promoCode"
      :error-key="promoErrorKey"
      @apply="applyPromo"
      @remove="removePromo"
    />

    <!-- ── BUY TAB ── -->
    <template v-if="tab === 'buy' && !loading">
      <!-- inline settle banner (mock) -->
      <div v-if="justGranted !== null" class="gr-granted">
        <img :src="runesIcon" class="w-6 h-6 gr-gem" alt="" />
        <!-- <i18n-t> so the emphasis stays ON the amount wherever a translator
             puts it in the sentence; a split string would pin English order. -->
        <i18n-t keypath="shop.granted" tag="span" scope="global">
          <template #amount><strong class="text-gold-bright">+{{ formatNumber(justGranted) }}</strong></template>
        </i18n-t>
      </div>

      <div class="gr-shelf">
        <div
          v-for="item in shelfPacks" :key="item.p.id"
          class="gr-relic"
          :class="{ 'gr-relic--hero': item.hero, 'guide-glow': props.spotlight }"
          :style="{ '--gr-c': item.look.color, '--cb': item.hero ? 'var(--color-gold-bright)' : 'var(--color-gold)' }"
        >
          <div v-if="item.hero" class="gr-ember-pool" aria-hidden="true"></div>

          <div v-if="item.look.ribbon" class="gr-plaque bound-frame">
            <span class="gr-plaque-ic">{{ item.look.ribbonIcon }}</span>
            <span class="gr-plaque-txt gilt-text">{{ t(item.look.ribbon) }}</span>
          </div>

          <div class="gr-relic-art"><img :src="item.look.art" :alt="item.p.name" /></div>

          <div class="gr-relic-figure">
            <div class="gr-relic-amount gilt-text">{{ formatNumber(item.p.bars) }}</div>
            <div class="gr-relic-unit">{{ t('shop.runesUnit') }}</div>
          </div>

          <div class="gr-slate"><span class="gr-slate-fig">{{ price(item.p.priceCents) }}</span></div>

          <button
            class="btn-forge btn-forge--sm gr-buy" :class="buyVariant(item.look.btn)"
            :disabled="busy !== null"
            @click="buy(item.p)"
          >{{ busy === item.p.id ? t('shop.processing') : t('shop.buyNow') }}</button>
        </div>
      </div>

      <!-- custom amount — buy any number of bars at the base rate -->
      <div v-if="custom" class="gr-counting surf-sunken">
        <div class="gr-custom-head">
          <div class="font-heading text-lg gilt-text">{{ t('shop.custom.title') }}</div>
          <div class="font-body text-xs text-text-dim">{{ t('shop.custom.desc', { min: formatNumber(custom.minBars), max: formatNumber(custom.maxBars) }) }}</div>
        </div>
        <div class="gr-custom-body">
          <input
            type="range" class="gr-slider"
            :min="custom.minBars" :max="custom.maxBars" step="50"
            v-model.number="customBars"
          />
          <div class="gr-custom-input">
            <img :src="runesIcon" class="w-4 h-4 shrink-0" alt="" />
            <input
              type="number" class="gr-custom-num"
              :min="custom.minBars" :max="custom.maxBars" v-model.number="customBars"
            />
            <span class="font-label uppercase tracking-wider text-[10px] text-text-dim">{{ t('shop.custom.unit') }}</span>
          </div>
          <div class="gr-slate gr-slate--custom"><span class="gr-slate-fig">{{ price(customCents) }}</span></div>
          <button
            class="btn-forge btn-forge--rune btn-forge--sm gr-buy gr-custom-buy"
            :disabled="busy !== null || !customValid"
            @click="buyCustom"
          >{{ busy === 'custom' ? t('shop.processing') : t('shop.custom.buy') }}</button>
        </div>
        <p v-if="!customValid" class="font-body text-[11px] text-error mt-1">
          {{ t('shop.custom.invalid', { min: formatNumber(custom.minBars), max: formatNumber(custom.maxBars) }) }}
        </p>
      </div>

      <p class="mt-5 text-center font-body italic text-[11px] text-text-dim/70 leading-snug">
        {{ t('shop.footnote.buy') }}
        <span v-if="testMode">{{ t('shop.footnote.testMode') }}</span>
      </p>
    </template>

    <!-- ── SPECIAL OFFERS TAB (discounted limited-time bundles) ── -->
    <template v-else-if="tab === 'deals'">
      <div v-if="justGranted !== null" class="gr-granted">
        <img :src="runesIcon" class="w-6 h-6 gr-gem" alt="" />
        <!-- <i18n-t> so the emphasis stays ON the amount wherever a translator
             puts it in the sentence; a split string would pin English order. -->
        <i18n-t keypath="shop.granted" tag="span" scope="global">
          <template #amount><strong class="text-gold-bright">+{{ formatNumber(justGranted) }}</strong></template>
        </i18n-t>
      </div>

      <div class="gr-deals-head">
        <div>
          <div class="font-heading text-lg text-gold">{{ t('shop.deals.title') }}</div>
          <!-- Was "More runes per euro" — the copy named a currency the player may
               not be seeing. Kept currency-neutral rather than interpolated. -->
          <div class="font-body text-xs text-text-dim">{{ t('shop.deals.desc') }}</div>
        </div>
        <div class="gr-countdown" :aria-label="t('shop.deals.timeLeft')">
          <span class="gr-cd-label">{{ t('shop.deals.endsIn') }}</span>
          <span class="gr-cd-clock">{{ countdown }}</span>
        </div>
      </div>

      <div class="gr-deal-grid">
        <div v-for="o in offers" :key="o.id" class="gr-deal" :style="{ '--gr-c': dealTier(o.bars).color }">
          <div v-if="o.tag" class="gr-plaque gr-plaque--deal bound-frame">
            <span class="gr-plaque-txt gilt-text">{{ o.tag }}</span>
          </div>
          <span class="gr-deal-save">&minus;{{ o.savings }}%</span>
          <div class="gr-deal-art"><img :src="dealTier(o.bars).art" :alt="o.name" /></div>
          <div class="gr-deal-bars">
            <img :src="runesIcon" class="w-3.5 h-3.5 shrink-0" alt="" />
            <b class="gilt-text">{{ formatNumber(o.bars) }}</b>
          </div>
          <div class="gr-deal-name">{{ o.name }}</div>
          <div class="gr-slate gr-slate--deal">
            <s>{{ price(o.originalCents) }}</s>
            <span class="gr-slate-fig">{{ price(o.priceCents) }}</span>
          </div>
          <button
            class="btn-forge btn-forge--rune btn-forge--sm gr-buy gr-deal-buy"
            :disabled="busy !== null"
            @click="buyOffer(o)"
          >{{ busy === o.id ? t('shop.processing') : t('shop.grab') }}</button>
        </div>
      </div>

      <p v-if="testMode" class="mt-5 text-center font-body italic text-[11px] text-text-dim/70 leading-snug">
        {{ t('shop.footnote.testMode') }}
      </p>
    </template>

    <!-- Purchases, invoices, and the full transaction ledger now live in
         The Back Office (Settings → Billing) — this shop is for buying only. -->
    <p v-if="tab === 'buy' && !loading" class="mt-2 text-center font-body text-[11px] text-text-dim/60">
      <i18n-t keypath="shop.footnote.receipts" tag="span" scope="global">
        <template #location><span class="text-gold/80">{{ t('shop.footnote.receiptsLocation') }}</span></template>
      </i18n-t>
    </p>
  </section>
</template>

<style scoped>
.gr-gem { animation: gr-gem-pulse 2.6s ease-in-out infinite; }
@keyframes gr-gem-pulse {
  0%, 100% { filter: drop-shadow(0 0 2px rgba(227, 200, 120, 0.25)); transform: scale(1); }
  50% { filter: drop-shadow(0 0 9px rgba(227, 200, 120, 0.75)); transform: scale(1.06); }
}

/* The caption used to sit on a 5%-opacity tint, i.e. effectively straight on the
   shop artwork behind it — which is why it read as barely-there no matter what
   size it was. Now a carved slot on the opaque --sunken ground with the gold
   identity chased in as a rim, matching the .st-plate / .st-badge grammar the
   rest of the design system uses. A stable dark ground is what makes the caption
   legible; the type size was never the real problem.
   flex:none so "Saldo" / "Egyenleg" widen the plate instead of being squeezed. */
.gr-balance {
  flex: none;
  display: flex; align-items: center; gap: 8px;
  padding: 8px 14px;
  border: 0;
  /* hewn slot, matching .cs-balance — the house form for this plate */
  clip-path: var(--clip-hewn);
  background:
    linear-gradient(rgba(200, 162, 76, 0.10), rgba(200, 162, 76, 0.10)),
    var(--sunken);
  box-shadow:
    var(--rim-ice-sunken),
    inset 0 0 0 1px color-mix(in srgb, var(--color-gold) 26%, transparent);
}

/* Both notices below are carved slots with a struck tone tick in the leading
   edge — the same plate grammar as Settings' status plates — not tinted boxes
   with a coloured side border (that was their pre-retheme form). */
.gr-granted {
  position: relative;
  display: flex; align-items: center; justify-content: center; gap: 10px;
  margin-bottom: 16px;
  padding: 10px 16px 10px 22px;
  clip-path: var(--clip-hewn);
  background: var(--sunken);
  box-shadow: var(--rim-ice-sunken);
  font-family: var(--font-body, serif);
  font-size: 15px;
  color: var(--color-text);
}
.gr-granted::before {
  content: '';
  position: absolute; left: 7px; top: 50%; width: 3px; height: 58%;
  transform: translateY(-50%);
  background: var(--color-gold);
  filter: drop-shadow(0 0 6px color-mix(in srgb, var(--color-gold) 70%, transparent));
}

/* KYC gate notice — the amber plate */
.gr-kyc {
  position: relative;
  display: flex; align-items: center; gap: 12px;
  margin-bottom: 16px;
  padding: 10px 16px 10px 22px;
  clip-path: var(--clip-hewn);
  background: var(--sunken);
  box-shadow: var(--rim-ice-sunken);
  font-family: var(--font-body, serif);
  font-size: 14px;
  color: var(--color-text);
}
.gr-kyc::before {
  content: '';
  position: absolute; left: 7px; top: 50%; width: 3px; height: 58%;
  transform: translateY(-50%);
  background: #d69e2e;
  filter: drop-shadow(0 0 6px color-mix(in srgb, #d69e2e 70%, transparent));
}
.gr-kyc-badge {
  flex: none;
  padding: 3px 7px;
  border: 1px solid #d69e2e;
  border-radius: 2px;
  font-family: var(--font-label, sans-serif);
  font-size: 10px; letter-spacing: 0.08em;
  color: #d69e2e;
}

/* ── THE HOARD SHELF — treasure on a torchlit shelf, not tier cards. The three
   row heights below (--row-*) are the alignment anchor the brief asks for: every
   relic's slate and buy button land on the exact same y because every relic
   reads the SAME custom property, not a percentage tuned per element. */
.gr-shelf {
  --row-art: 118px;
  --row-name: 54px;
  --row-slate: 46px;
  --row-buy: 46px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  column-gap: 14px;
  row-gap: 34px;
  padding: 66px 6px 6px;
}
@media (max-width: 900px) { .gr-shelf { grid-template-columns: repeat(2, 1fr); } }
/* Mobile: stay a 2-col grid all the way to 320px (a 1-col fallback here was the
   reported bug — one pack per screen on a 4-tier shelf) and shrink the shelf
   furniture so a card is comfortable in a ~140px half-width column. */
@media (max-width: 700px) {
  .gr-shelf {
    --row-art: 100px;
    --row-name: 40px;
    --row-slate: 38px;
    --row-buy: 38px;
    column-gap: 10px;
    row-gap: 42px;
    padding: 44px 4px 4px;
  }
  .gr-relic { padding: 10px 6px 10px; row-gap: 4px; }
  .gr-relic-art { width: 84px; height: 84px; }
  .gr-relic--hero .gr-relic-art { width: 118px; height: 118px; }
  .gr-relic-amount { font-size: 20px; }
  .gr-relic--hero .gr-relic-amount { font-size: 22px; }
  .gr-relic-unit { font-size: 9px; }
  /* scale, don't reflow — keeps the plaque centred over the card instead of
     letting "BEST VALUE" spill past a half-width column */
  .gr-plaque { padding: 4px 8px 5px; gap: 3px; transform: translate(-50%, -62%) scale(0.86); }
  .gr-slate { min-width: 0; padding: 6px 10px; }
  .gr-slate-fig { font-size: 13px; }
}

.gr-relic {
  position: relative;
  isolation: isolate;
  display: grid;
  grid-template-rows: var(--row-art) var(--row-name) var(--row-slate) var(--row-buy);
  align-items: center;
  justify-items: center;
  row-gap: 6px;
  padding: 16px 10px 14px;
  background: var(--tex-forge), var(--plate);
  background-size: 150px 150px, auto;
  background-blend-mode: soft-light, normal;
  border: 1px solid #04060a;
  box-shadow: var(--rim-ice);
  filter: saturate(0.85);
  transition: transform 200ms ease, filter 200ms ease;
}
.gr-relic:hover { transform: translateY(-4px); filter: saturate(0.96); }

/* the best-value pack — the one thing on this shelf that gets to be warm */
.gr-relic--hero {
  filter: none;
  box-shadow:
    inset 0 0 0 1px color-mix(in srgb, var(--color-gold) 45%, transparent),
    var(--rim-ice),
    0 0 30px color-mix(in srgb, var(--color-ember) 26%, transparent);
}
.gr-relic--hero:hover { transform: translateY(-4px); filter: none; }

/* the ember pool it stands in — a radial gradient, not a flat glow, so it
   pools rather than paints; drop-shadow (not box-shadow) so it still shows
   once the treasure art itself gets clipped/scaled. */
.gr-ember-pool {
  position: absolute;
  left: 50%; top: calc(var(--row-art) - 34px);
  width: 210px; height: 130px;
  transform: translateX(-50%);
  z-index: 0;
  pointer-events: none;
  background: radial-gradient(ellipse 55% 60% at 50% 55%,
    color-mix(in srgb, var(--color-ember) 65%, transparent) 0%, transparent 72%);
  filter: blur(2px);
}

/* a jarl's decree — a small gilt-framed plaque hung off the top edge of the
   shelf item (bound-frame supplies the gilt rim + corner knots, app.css).
   top: 0 relative to .gr-relic means every plaque on the shelf sits at the
   same y, whatever is happening with the art beneath it. */
.gr-plaque {
  position: absolute;
  top: 0; left: 50%;
  transform: translate(-50%, -62%);
  z-index: 6;
  display: inline-flex; align-items: center; gap: 5px;
  padding: 6px 14px 7px;
  background: var(--tex-forge), var(--plate);
  background-size: 120px 120px, auto;
  background-blend-mode: soft-light, normal;
  white-space: nowrap;
}
.gr-plaque-ic { font-size: 10px; line-height: 1; color: var(--color-gold-bright); }
.gr-plaque-txt {
  font-family: var(--font-label);
  font-size: 10px; font-weight: 600;
  letter-spacing: 0.07em; text-transform: uppercase;
}

.gr-relic-art {
  grid-row: 1;
  align-self: end;
  position: relative; z-index: 3;
  width: 92px; height: 92px;
  filter: saturate(0.72) brightness(0.92) drop-shadow(0 8px 16px rgba(6, 10, 18, 0.7));
}
.gr-relic-art img { width: 100%; height: 100%; object-fit: contain; }

/* HUGE, and standing taller than its own shelf slot — the overflow above the
   row (172px art in a 118px track, bottom-anchored) is the pedestal: the
   piece is too big for the shelf, which is the point. */
.gr-relic--hero .gr-relic-art {
  width: 176px; height: 176px;
  z-index: 5;
  filter:
    saturate(1.12) brightness(1.05)
    drop-shadow(0 10px 22px rgba(20, 12, 6, 0.6))
    drop-shadow(0 0 38px color-mix(in srgb, var(--color-ember) 55%, transparent));
}

.gr-relic-figure {
  grid-row: 2;
  position: relative;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 2px;
}
/* the pedestal cap — a carved stone lip, gilt on its top edge, that the
   hero's figure is struck onto. This is what turns "bigger art" into
   "raised on a dais" instead of just "zoomed in". */
.gr-relic--hero .gr-relic-figure::before {
  content: '';
  position: absolute;
  left: 4%; right: 4%; top: -8px;
  height: 12px; z-index: 0;
  background: var(--tex-stone), linear-gradient(180deg, #384252 0%, #171d26 100%);
  background-size: 90px 90px, auto;
  background-blend-mode: soft-light, normal;
  box-shadow:
    inset 0 1px 0 rgba(245, 218, 150, 0.4),
    0 3px 8px rgba(0, 0, 0, 0.55);
}
.gr-relic-amount {
  position: relative; z-index: 1;
  font-family: var(--font-heading);
  font-size: 25px; font-weight: 700; line-height: 1;
  font-variant-numeric: tabular-nums;
}
.gr-relic--hero .gr-relic-amount { font-size: 33px; }
.gr-relic-unit {
  position: relative; z-index: 1;
  font-family: var(--font-label);
  font-size: 9.5px; letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--color-text-dim);
}

/* prices struck into a shared slate: same clip, same recess, same y — and
   hugging the right edge of the pack's own column, so the row reads as a
   ledger rather than a scatter of floating price tags. */
.gr-slate {
  grid-row: 3;
  justify-self: end;
  align-self: center;
  position: relative; z-index: 1;
  min-width: 74px;
  padding: 9px 14px;
  clip-path: var(--clip-hewn);
  background: var(--sunken);
  box-shadow: var(--rim-ice-sunken);
  text-align: right;
}
.gr-slate-fig {
  font-family: var(--font-heading);
  font-size: 15px; font-weight: 900;
  color: var(--color-gold-bright);
  font-variant-numeric: tabular-nums;
}

.gr-buy { grid-row: 4; z-index: 1; width: 100%; }

/* ── the counting slate — the custom-amount panel is a stone ledger now ── */
.gr-counting {
  margin-top: 18px;
  padding: 16px 18px;
  clip-path: var(--clip-hewn);
}
.gr-custom-head { margin-bottom: 10px; }
.gr-custom-body { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.gr-slider { flex: 1 1 240px; min-width: 180px; accent-color: var(--color-gold-bright); cursor: pointer; }
.gr-custom-input { display: inline-flex; align-items: center; gap: 8px; }
/* the chisel slot the amount is cut into */
.gr-custom-num {
  width: 88px;
  padding: 7px 10px;
  border: 0;
  outline: none;
  clip-path: var(--clip-hewn);
  background: var(--sunken);
  box-shadow: var(--rim-ice-sunken);
  font-family: var(--font-heading, serif); font-size: 15px; color: var(--color-gold-bright);
  font-variant-numeric: tabular-nums;
  transition: box-shadow 160ms ease;
}
.gr-custom-num:focus {
  box-shadow:
    var(--rim-ice-sunken),
    inset 0 0 0 2px color-mix(in srgb, var(--color-aurora) 70%, transparent),
    0 0 14px color-mix(in srgb, var(--color-aurora) 45%, transparent);
}
.gr-custom-num::-webkit-outer-spin-button, .gr-custom-num::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.gr-slate--custom { grid-row: auto; justify-self: auto; align-self: auto; min-width: 84px; }
.gr-custom-buy { width: auto; min-width: 130px; margin-top: 0; padding: 9px 18px; }

/* the panel stays one full-width block on mobile (per spec) — just tighten it
   so it does not tower above the shelf */
@media (max-width: 700px) {
  .gr-counting { margin-top: 14px; padding: 12px 12px; }
  .gr-custom-body { gap: 10px; }
}

/* ── special offers ── */
.gr-deals-head {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  margin-bottom: 16px; flex-wrap: wrap;
}
/* Same treatment as .gr-balance — an 8% tint over the deals artwork left the
   "Ends in" caption competing with whatever was behind it.
   flex:none — a longer label must push the frame wider rather than let the
   justify-between row squeeze the clock. */
.gr-countdown {
  flex: none;
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 12px;
  border: 0;
  /* hewn slot, matching .cs-balance — the house form for this plate */
  clip-path: var(--clip-hewn);
  background:
    linear-gradient(rgba(179, 65, 62, 0.16), rgba(179, 65, 62, 0.16)),
    var(--sunken);
  box-shadow:
    var(--rim-ice-sunken),
    inset 0 0 0 1px rgba(179, 65, 62, 0.5);
}
/* 9px in --color-text-dim (#8b9aab) was barely legible, and "Ends in" is the
   shortest form this label takes — "Slutter om" / "Kończy się za" are twice the
   length. Bumped a step, brightened to the readable micro-label tone the rest of
   the design system uses (#bccbdb, cf. .st-label), tracking eased so the longer
   translations do not sprawl, and pinned to one line. */
.gr-cd-label {
  flex: none; white-space: nowrap;
  font-family: var(--font-label, sans-serif);
  font-size: 10px; letter-spacing: 0.06em; text-transform: uppercase;
  color: #bccbdb;
}
.gr-cd-clock { font-family: var(--font-heading, serif); font-size: 17px; font-variant-numeric: tabular-nums; color: #e6a24a; letter-spacing: 0.04em; }

.gr-deal-grid {
  /* row gap must clear the plaque overhang (~15px above each card, see
     .gr-plaque--deal) — with the full 20-deal catalog this grid runs five
     rows deep, so every row below the first needs the headroom */
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 26px 12px;
  padding-top: 22px;
}
@media (max-width: 1100px) { .gr-deal-grid { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 720px) { .gr-deal-grid { grid-template-columns: repeat(2, 1fr); } }
/* This is the 20-deal catalog the brief calls out — stays a 2-col grid all the
   way to 320px (no 1-col collapse) so the "kilometre of scroll" gets halved. */
@media (max-width: 700px) {
  .gr-deal-grid { gap: 20px 8px; }
  .gr-deal { padding: 10px 8px 8px; }
  .gr-deal-art { width: 62px; height: 62px; margin-top: 4px; }
  .gr-deal-bars { margin-top: 8px; }
  .gr-deal-bars b { font-size: 18px; }
  .gr-deal-name { font-size: 9px; min-height: 20px; margin-top: 4px; }
  .gr-plaque--deal { padding: 4px 12px 5px; transform: translate(-50%, -58%) scale(0.85); }
  .gr-deal-save { padding: 3px 7px; font-size: 11px; }
  .gr-slate--deal { margin-top: 4px; margin-bottom: 8px; gap: 6px; }
}

.gr-deal {
  position: relative;
  display: flex; flex-direction: column; align-items: center;
  padding: 14px 12px 12px;
  background: var(--tex-forge), var(--plate);
  background-size: 150px 150px, auto;
  background-blend-mode: soft-light, normal;
  border: 1px solid #04060a;
  box-shadow: var(--rim-ice);
  transition: transform 160ms ease;
}
.gr-deal:hover { transform: translateY(-3px); }
.gr-plaque--deal {
  transform: translate(-50%, -58%);
  /* the plaque is now rare (see tagFor in gold-packages.ts), so it can afford
     to breathe — the word was previously jammed against both cut edges */
  padding: 5px 20px 6px;
  gap: 7px;
}
.gr-deal-save {
  position: absolute; top: 8px; right: 8px; z-index: 2;
  padding: 4px 9px;
  clip-path: var(--clip-hewn);
  background: var(--sunken);
  box-shadow: var(--rim-ice-sunken);
  font-family: var(--font-heading, serif); font-size: 12px; font-weight: 900;
  color: var(--color-ember);
  font-variant-numeric: tabular-nums;
}
.gr-deal-art {
  width: 78px; height: 78px;
  margin-top: 6px;
  position: relative; z-index: 1;
  filter: saturate(0.85) drop-shadow(0 5px 12px rgba(6, 10, 18, 0.7));
}
.gr-deal-art img { width: 100%; height: 100%; object-fit: contain; }
.gr-deal-bars {
  display: inline-flex; align-items: center; gap: 5px; margin-top: 10px;
  position: relative; z-index: 1;
  font-family: var(--font-heading, serif);
}
.gr-deal-bars b { font-size: 21px; font-weight: 700; line-height: 1; font-variant-numeric: tabular-nums; }
.gr-deal-name {
  margin-top: 5px; text-align: center;
  position: relative; z-index: 1;
  font-family: var(--font-label, sans-serif); font-size: 10px; letter-spacing: 0.08em;
  text-transform: uppercase; color: var(--color-text-dim); min-height: 24px;
}
.gr-slate--deal {
  grid-row: auto; justify-self: auto; align-self: auto;
  display: flex; align-items: baseline; gap: 8px;
  margin-top: 6px; margin-bottom: 12px;
  min-width: 0;
}
.gr-slate--deal s { font-family: var(--font-body, serif); font-size: 11px; color: var(--color-text-dim); }
.gr-deal-buy { width: 100%; margin-top: auto; position: relative; z-index: 1; }

.gr-spinner {
  width: 32px; height: 32px; margin: 0 auto;
  border: 3px solid rgba(200, 162, 76, 0.2);
  border-top-color: var(--color-gold);
  border-radius: 50%;
  animation: gr-spin 0.8s linear infinite;
}
@keyframes gr-spin { to { transform: rotate(360deg); } }

@media (prefers-reduced-motion: reduce) {
  .gr-gem { animation: none; }
  .gr-spinner { animation: none; border-top-color: var(--color-gold); }
  .gr-relic, .gr-relic:hover, .gr-relic--hero:hover, .gr-deal, .gr-deal:hover { transition: none; transform: none; }
}
</style>
